import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { QualificationForm } from "../../components/qualifications/QualificationForm";
import api from "../../utils/api";
import { User } from "../../types";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Card } from "../../components/ui/card";
import { ShieldAlert } from "lucide-react";

export default function AddQualificationPage() {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const { addQualification, fetchQualifications } = useData();
  const [person, setPerson] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(!!personId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ability = useAbility(AbilityContext);

  useEffect(() => {
    if (personId) {
      setIsLoadingUser(true);
      api.get(`/api/users/${personId}`, { skipErrorToast: true })
        .then(response => {
          if (response.success) {
            setPerson(response.user);
          }
        })
        .catch(err => console.error("Error fetching user:", err))
        .finally(() => setIsLoadingUser(false));
    }
  }, [personId]);

  const handleSubmit = async (payload: any, selectedFiles: File[]) => {
    setIsSubmitting(true);
    try {
      const response = await addQualification(payload);
      const qId = response?.qualification?.id;

      if (qId && selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', file);
          uploadFormData.append('context', 'qualifications'); // Pass context for S3 folder structure

          try {
            const uploadResult = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });

            if (uploadResult.url) {
              await api.post('/api/qualifications/attachments', {
                qualificationId: qId,
                filename: file.name,
                url: uploadResult.url,
                key: uploadResult.key || uploadResult.filename, // Use S3 key if available
                size: uploadResult.size,
                mimetype: uploadResult.mimetype
              }, { skipErrorToast: true });
            }
          } catch (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      // Identification of success handled by DataContext
      // toast.success("Qualification added successfully");
      await fetchQualifications();
      
      // Navigate to the person's page if it was accessible and provided in URL, otherwise back to register
      if (personId && person) {
        navigate(`/people/${personId}?tab=qualifications`);
      } else {
        navigate(`/qualifications`);
      }
    } catch (error) {
      console.error("Submit error:", error);
      // Error already handled by api utility (or re-thrown and handled here)
      // toast.error("An error occurred while adding the qualification");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ability.can(ACTIONS.CREATE, RESOURCES.QUALIFICATION)) {
    return (
      <div className="container mx-auto py-20 flex justify-center">
        <Card className="border-slate-200 shadow-lg p-12 text-center bg-white max-w-2xl">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="bg-red-50 p-4 rounded-full">
              <ShieldAlert className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Access Denied</h2>
            <p className="text-slate-500 max-w-md mx-auto">
              You do not have the required permissions to add qualifications. 
              Please contact your system administrator if you believe this is an error.
            </p>
            <Button variant="outline" onClick={() => navigate("/qualifications")}>Back to Register</Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoadingUser && !person) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-2 text-slate-500 font-medium">Loading person details...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="space-y-4">
        <button 
          onClick={handleCancel}
          className="flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Qualifications
        </button>
        
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Add Qualification</h1>
          <p className="text-slate-500">
            Add a new qualification for <span className="font-semibold text-slate-900">{person?.name || "the selected person"}</span>
          </p>
        </div>
      </div>

      <div className="mt-8">
        <QualificationForm 
          personId={personId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isSubmitting}
          submitLabel="Add Qualification"
          isPage={true}
        />
      </div>
    </div>
  );
}
